import { cloneElement, type ReactElement, type ReactNode, useEffect, useRef } from 'react';
import { notifications } from '@mantine/notifications';
import { useLocation } from 'react-router-dom';

import { PwaUpdateContext } from '@/components/pwa/pwaUpdateContext';
import { usePwaUpdate } from '@/components/pwa/usePwaUpdate';
import { I, type IconProps } from '@/components/ri/icons';
import { isPaymentPage } from '@/lib/paymentTelemetryPrivacy';

const TOAST_ID = 'pwa-update-ready';

export function PwaUpdateProvider({ children }: { children: ReactNode }) {
  const onCustomerSurface = isPaymentPage(useLocation().pathname);
  const state = usePwaUpdate(!onCustomerSurface);
  const { needRefresh } = state;
  const toasted = useRef(false);

  // One attention grab per detected build; afterwards the header indicator is the only reminder.
  // A toast still on screen is pulled when the route moves onto a customer surface.
  useEffect(() => {
    if (onCustomerSurface) {
      notifications.hide(TOAST_ID);
      return;
    }
    if (!needRefresh || toasted.current) return;
    toasted.current = true;
    notifications.show({
      id: TOAST_ID,
      icon: cloneElement(I.refresh as ReactElement<IconProps>, { size: 14 }),
      color: 'var(--accent)',
      title: 'New version ready',
      message: "Finish what you're on, then refresh from the header whenever.",
      autoClose: 8000,
    });
  }, [needRefresh, onCustomerSurface]);

  return <PwaUpdateContext.Provider value={state}>{children}</PwaUpdateContext.Provider>;
}
